/**
 * Nearest-x hit-testing and keyboard walking for the three line charts.
 *
 * WHY NOT PER-POINT HOVER TARGETS. These series run to 366 points across a
 * ~480-unit plot: a point owns about 1.3 units of x, which on a typical render
 * is two or three physical pixels. Per-point `<circle>` targets would be a chart
 * you cannot hover on purpose, and adding invisible fat targets would put 366
 * elements in the DOM per series to do what one arithmetic expression does. So
 * the whole plot is one target, the pointer's x is mapped back through the same
 * geometry the chart draws with, and the result is snapped to the closest day.
 *
 * WHY THE KEYBOARD PATH EXISTS AT ALL (FR1.5). On the line charts the tooltip is
 * not a convenience — the exact per-day figures exist NOWHERE else on the page.
 * The legend carries each series' window total and the axis carries the peak;
 * "how many entered Pursuing on the 26th" is only answerable by hovering. Data
 * that is only reachable by pointer is data withheld from anyone who does not
 * use one, so the chart takes focus, arrows walk the days, and the focused
 * day's values go into a polite live region.
 *
 * The live region is fed ONLY on keyboard interaction. A screen-reader user is
 * not moving a pointer, and announcing every mouse-driven change would turn one
 * careless sweep across a year into 366 interruptions.
 */

import {
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useState,
} from "react";

/** The chart's own viewBox geometry — the same constants it draws with. */
export interface PlotGeometry {
  /** viewBox width, in viewBox units. */
  width: number;
  padLeft: number;
  padRight: number;
  /**
   * viewBox height. Only needed to map the pointer's y back into viewBox
   * space for the nearest-SERIES highlight; charts that do not highlight may
   * leave it out.
   */
  height?: number;
}

/**
 * Snap a viewBox x coordinate to the index of the closest plotted point.
 *
 * Pure and exported for its own test: this is the entire hit-test, and asserting
 * it through a rendered SVG would mean stubbing layout to check arithmetic.
 * Clamped at both ends, so the pointer inside the chart's left and right padding
 * still reads the first and last day rather than falling off the series.
 */
export function nearestIndex(
  viewBoxX: number,
  count: number,
  { width, padLeft, padRight }: PlotGeometry
): number {
  if (count <= 1) return 0;
  const step = (width - padLeft - padRight) / (count - 1);
  if (step <= 0) return 0;
  const raw = Math.round((viewBoxX - padLeft) / step);
  return Math.min(count - 1, Math.max(0, raw));
}

export interface NearestPoint {
  /** The focused point's index, or null when nothing is focused. */
  index: number | null;
  /**
   * True when the current index came from a key press — the flag the caller
   * uses to decide whether to feed its live region.
   */
  fromKeyboard: boolean;
  /**
   * Which half of the plot the focused point sits in. Callers pin the tooltip to
   * the OTHER side so it never covers the crosshair.
   */
  side: "left" | "right";
  /**
   * The pointer's y in viewBox units, or null when the focus did not come from
   * a pointer.
   *
   * This is what lets a chart say WHICH line the reader is looking at, without
   * giving up the whole-day crosshair. The tooltip still answers "how many
   * Identified against Pursuing on the 26th" in one read; the highlight answers
   * "and which of these is the line under my cursor". A keyboard reader has no
   * pointer, so there is no nearest series and nothing is highlighted — which
   * also keeps the live region below exactly as it was.
   */
  pointerY: number | null;
  /** Spread onto the positioned wrapper that tightly encloses the `<svg>`. */
  surfaceProps: {
    onPointerMove(event: PointerEvent<HTMLElement>): void;
    onPointerLeave(): void;
    onKeyDown(event: KeyboardEvent<HTMLElement>): void;
    onFocus(event: FocusEvent<HTMLElement>): void;
    onBlur(): void;
  };
}

const WALK_KEYS = new Set(["ArrowLeft", "ArrowRight", "Home", "End"]);

export function useNearestPoint(count: number, geometry: PlotGeometry): NearestPoint {
  const [index, setIndex] = useState<number | null>(null);
  const [fromKeyboard, setFromKeyboard] = useState(false);
  const [pointerY, setPointerY] = useState<number | null>(null);

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const box = event.currentTarget.getBoundingClientRect();
      // Zero width means the element is not laid out — hidden, detached, or
      // happy-dom, which reports every rect as zero. Reading a coordinate off it
      // would snap every pointer move to day one.
      if (box.width <= 0 || count === 0) return;
      const viewBoxX = ((event.clientX - box.left) / box.width) * geometry.width;
      setIndex(nearestIndex(viewBoxX, count, geometry));
      // Height is optional, and so is the highlight it feeds: a chart that does
      // not pass one gets the crosshair it always had.
      setPointerY(
        geometry.height !== undefined && box.height > 0
          ? ((event.clientY - box.top) / box.height) * geometry.height
          : null
      );
      setFromKeyboard(false);
    },
    [count, geometry]
  );

  const onPointerLeave = useCallback(() => {
    // Only the POINTER's own focus is dropped. A keyboard reader who tabbed in
    // and then jogged the mouse would otherwise lose their place.
    setPointerY(null);
    setFromKeyboard((keyboard) => {
      if (!keyboard) setIndex(null);
      return keyboard;
    });
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (count === 0 || !WALK_KEYS.has(event.key)) return;
      // Walking with the keys drops any pointer position, so the highlight does
      // not survive as a leftover from a mouse that has since stopped moving.
      setPointerY(null);
      // Arrow keys scroll the page by default, which on a chart the reader has
      // just focused would walk the day and slide it off screen at once.
      event.preventDefault();
      setFromKeyboard(true);
      setIndex((current) => {
        // Clamped HERE too, not only on read: with a stale index of 100 over a
        // 3-point series the display already shows 2, but `ArrowLeft` from the
        // raw 100 lands on 99 → clamps to 2 → nothing appears to move on the
        // reader's first press. Self-healing, but the press is wasted.
        const from = Math.min(current ?? 0, Math.max(0, count - 1));
        if (event.key === "Home") return 0;
        if (event.key === "End") return count - 1;
        const next = event.key === "ArrowRight" ? from + 1 : from - 1;
        return Math.min(count - 1, Math.max(0, next));
      });
    },
    [count]
  );

  const onFocus = useCallback((event: FocusEvent<HTMLElement>) => {
    // A CLICK FOCUSES THIS DIV TOO. `tabIndex={0}` means a plain pointer click
    // fires `onFocus` right after `onPointerMove`, so an unconditional
    // `fromKeyboard = true` here did two wrong things on every click: it masked
    // `pointerY` (the per-series highlight vanished until the mouse was jiggled)
    // and it fed the live region on a POINTER interaction, contradicting this
    // file's own contract above.
    //
    // `:focus-visible` is exactly the "focus the browser thinks deserves a ring"
    // signal — keyboard yes, click no. Not wrapped in a try/catch: `matches`
    // does not throw here, and the browser floor is well past the selector's
    // ship date (Chrome 86 / Firefox 85 / Safari 15.4).
    //
    // A NOTE FOR TESTS, measured against this repo's happy-dom 15.11.7:
    // it implements `:focus-visible` as a plain ALIAS OF `:focus`
    // (`SelectorItem.js:312-316` — it returns true iff the element is
    // `document.activeElement`). So it is not "always false" and it is not
    // "always keyboard": which branch a test takes depends on whether the
    // element is really focused.
    //
    //   `fireEvent.focus(el)`  dispatches the event without focusing → POINTER branch
    //   `el.focus()`           makes it activeElement            → KEYBOARD branch
    //
    // Either branch can also be forced by stubbing `matches` on the element,
    // which is what the tests do so the intent is explicit rather than a
    // side effect of how focus was triggered.
    const keyboardDriven = event.currentTarget.matches(":focus-visible");

    // Focus alone reveals the first point rather than nothing at all: a chart
    // that takes focus and shows no change gives a reader no signal that arrows
    // would do anything. `End` jumps to the recent end in one press, which is
    // why starting at the far one is not a 365-keystroke walk.
    //
    // This happens for POINTER focus too, and deliberately. `:focus-visible` is
    // false for a touch-initiated focus in Chrome and Safari, so returning early
    // here would mean a tap on a tablet reveals nothing at all unless the
    // browser happened to emit a `pointermove` first — and the per-point figures
    // exist nowhere else on the page (FR1.5 above). Revealing a point is safe on
    // every input; it is `fromKeyboard` that must not be claimed by a click.
    setIndex((current) => current ?? 0);
    if (keyboardDriven) setFromKeyboard(true);
  }, []);

  const onBlur = useCallback(() => {
    setIndex(null);
    setPointerY(null);
    setFromKeyboard(false);
  }, []);

  // CLAMPED ON READ, not only when it was set. The setters clamp against the
  // `count` of the moment; nothing re-clamps when the series SHRINKS under a
  // still-focused chart — a history navigation over a `?from=` change, or a
  // refetch returning fewer points. The callers then dereference
  // `days[focused]` and `points[focused]`, so a stale index is a `TypeError` and
  // a blank tab, not a garbled label. One line removes the class (PR #752).
  const clamped = index === null || count === 0 ? null : Math.min(index, count - 1);

  return {
    index: clamped,
    fromKeyboard,
    pointerY: fromKeyboard ? null : pointerY,
    // The midpoint, not the pointer's raw x: what matters is which half of the
    // PLOT the described point sits in.
    side: clamped !== null && clamped * 2 >= count ? "left" : "right",
    surfaceProps: { onPointerMove, onPointerLeave, onKeyDown, onFocus, onBlur },
  };
}

/**
 * The visible series whose plotted point is closest to the pointer.
 *
 * Generic over the series shape so both line charts share one implementation —
 * they had the same nine-line reduce differing only in which field names the
 * series (`stage` vs `method`), and each carried two `as number` assertions
 * because the null check above them did not narrow into the closure. Taking
 * `pointerY` as a non-nullable argument removes the casts by construction.
 *
 * Returns `undefined` when there is nothing to highlight, which is also what a
 * keyboard-driven focus produces: there is no pointer, so there is no nearest.
 */
export function nearestSeriesKey<T>(
  series: readonly T[],
  pointerY: number | null,
  keyOf: (item: T) => string,
  yOf: (item: T) => number
): string | undefined {
  if (pointerY === null || series.length === 0) return undefined;
  let closest = series[0];
  let bestDistance = Math.abs(yOf(closest) - pointerY);
  // Indexed rather than `series.slice(1)`: this runs on every `pointermove`,
  // and a copy per move is an allocation for nothing.
  for (let i = 1; i < series.length; i++) {
    const candidate = series[i];
    const distance = Math.abs(yOf(candidate) - pointerY);
    // Strict `<`: ties keep the EARLIER series, which is the one drawn first and
    // listed first in the legend. Without this, two lines overlapping at zero
    // would highlight whichever happened to come last (PR #752, Agent C).
    if (distance < bestDistance) {
      closest = candidate;
      bestDistance = distance;
    }
  }
  return keyOf(closest);
}
